/* ============================================
   HN Date
   Custom element that progressively enhances a
   native <input type="date">. The native input
   stays in the DOM (visually hidden, form-
   submittable) and a styled button trigger +
   custom calendar popup sit on top. We replace
   the browser's native picker entirely so the
   visual language matches the rest of the form
   (text inputs, <hn-select>) across all devices.

   Usage:
     <hn-date>
       <input type="date" name="purchase_date" id="x" required>
     </hn-date>

   Floating-label opt-in (matches <hn-select>):
     <div class="field">
       <hn-date class="hn-date--block">
         <input type="date" name="x" id="x" required>
       </hn-date>
       <label class="field__label" for="x">Purchase Date *</label>
     </div>

   Respects min and max attributes on the native
   input for disabling out-of-range days.
   ============================================ */

(function () {
  if (customElements.get('hn-date')) return;

  const CALENDAR_SVG =
    '<svg class="hn-date__icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none">' +
    '<rect x="3.5" y="5.5" width="17" height="15" rx="2" stroke="currentColor" stroke-width="1.5"/>' +
    '<path d="M3.5 10h17" stroke="currentColor" stroke-width="1.5"/>' +
    '<path d="M8 3.5v4M16 3.5v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
    '</svg>';

  const CHEVRON_LEFT_SVG =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none">' +
    '<path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  const CHEVRON_RIGHT_SVG =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none">' +
    '<path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  const MONTHS_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const MONTHS_LONG = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const DAYS_LONG = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday',
    'Thursday', 'Friday', 'Saturday',
  ];

  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  function toISO(d) {
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }

  function fromISO(iso) {
    // Parse YYYY-MM-DD as a *local* date. new Date(iso) would treat
    // a bare YYYY-MM-DD as UTC midnight, which shifts the day in
    // negative-UTC timezones. Splitting and using the local-date
    // constructor avoids that off-by-one bug.
    if (!iso) return null;
    const parts = iso.split('-');
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    return new Date(y, m - 1, d);
  }

  function formatISODate(iso) {
    if (!iso) return '';
    const d = fromISO(iso);
    if (!d) return iso;
    return MONTHS_SHORT[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
  }

  function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
  }

  function isBefore(a, b) {
    if (a.getFullYear() !== b.getFullYear()) return a.getFullYear() < b.getFullYear();
    if (a.getMonth() !== b.getMonth()) return a.getMonth() < b.getMonth();
    return a.getDate() < b.getDate();
  }

  function isAfter(a, b) {
    if (a.getFullYear() !== b.getFullYear()) return a.getFullYear() > b.getFullYear();
    if (a.getMonth() !== b.getMonth()) return a.getMonth() > b.getMonth();
    return a.getDate() > b.getDate();
  }

  let uidCounter = 0;

  class HnDate extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.viewYear = 0;
      this.viewMonth = 0;
      this.focusedDate = null;
      this._onDocPointerDown = this._onDocPointerDown.bind(this);
      this._onDocKeydown = this._onDocKeydown.bind(this);
      this._onWinChange = this._onWinChange.bind(this);
    }

    connectedCallback() {
      this.native = this.querySelector(':scope > input[type="date"]');
      if (!this.native) return;
      if (this._initialized) return;
      this._initialized = true;

      this.native.classList.add('hn-date__native');
      // Keep the native input out of the keyboard tab order. The
      // trigger button becomes the user-facing tab stop; the native
      // remains in the DOM for form submission only.
      this.native.setAttribute('tabindex', '-1');

      this._buildUI();
      this._bindEvents();
      this.syncFromNative();
      this._observeNative();
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this._onDocPointerDown, true);
      document.removeEventListener('keydown', this._onDocKeydown, true);
      window.removeEventListener('resize', this._onWinChange);
      window.removeEventListener('scroll', this._onWinChange, true);
      if (this._nativeObserver) {
        this._nativeObserver.disconnect();
        this._nativeObserver = null;
      }
      // If the popup was portaled to <body>, tear it down so it
      // doesn't leak after this element is removed from the DOM.
      if (this.popup && this.popup.parentNode === document.body) {
        document.body.removeChild(this.popup);
      }
    }

    _onWinChange() {
      if (this.isOpen) this._positionPopup();
    }

    _positionPopup() {
      // The popup is portaled to <body> when open (see open()) so we
      // position it with viewport-relative coordinates via inline
      // style. Doing this bypasses any ancestor stacking context
      // (e.g. the warranty form grid's transformed final state) or
      // overflow:hidden ancestor that would otherwise clip / hide
      // the popup behind a downstream section.
      const rect = this.trigger.getBoundingClientRect();
      const popupHeight = this.popup.offsetHeight || 360;
      const popupWidth = this.popup.offsetWidth || 300;
      const margin = 8;

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top;
      if (spaceBelow < popupHeight + 16 && spaceAbove > spaceBelow) {
        // Open upward when there isn't room below and there's more
        // room above (e.g. near the footer on mobile).
        top = rect.top - popupHeight - 6;
      } else {
        top = rect.bottom + 6;
      }

      // Align popup's left edge with the trigger's, then clamp to
      // the viewport so it never extends past the right or left edge.
      let left = rect.left;
      if (left + popupWidth > window.innerWidth - margin) {
        left = window.innerWidth - popupWidth - margin;
      }
      if (left < margin) left = margin;

      this.popup.style.position = 'fixed';
      this.popup.style.top = top + 'px';
      this.popup.style.left = left + 'px';
      this.popup.style.zIndex = '9999';
    }

    _observeNative() {
      // Mirror the native's disabled state on the trigger so external
      // callers (e.g. Dawn's recipient-form clearing fields when the
      // gift-card recipient checkbox is unchecked) can disable the
      // control via the standard input.disabled API.
      this.trigger.disabled = this.native.disabled;
      if (typeof MutationObserver !== 'function') return;
      this._nativeObserver = new MutationObserver(() => {
        this.trigger.disabled = this.native.disabled;
      });
      this._nativeObserver.observe(this.native, {
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }

    _buildUI() {
      const uid = ++uidCounter;
      const baseId = this.native.id || 'hn-date-' + uid;
      this._triggerId = baseId + '-trigger';
      this._popupId = baseId + '-popup';
      this._gridId = baseId + '-grid';
      this._titleId = baseId + '-title';

      // Trigger button
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'hn-date__trigger';
      this.trigger.id = this._triggerId;
      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-controls', this._popupId);

      const labelledBy = this._resolveLabelledBy();
      if (labelledBy) this.trigger.setAttribute('aria-labelledby', labelledBy);
      else if (this.native.getAttribute('aria-label')) {
        this.trigger.setAttribute('aria-label', this.native.getAttribute('aria-label'));
      }
      if (this.native.disabled) this.trigger.disabled = true;

      this.valueEl = document.createElement('span');
      this.valueEl.className = 'hn-date__value';
      this.trigger.appendChild(this.valueEl);
      this.trigger.insertAdjacentHTML('beforeend', CALENDAR_SVG);

      // Popup dialog
      this.popup = document.createElement('div');
      this.popup.className = 'hn-date__popup';
      this.popup.id = this._popupId;
      this.popup.setAttribute('role', 'dialog');
      this.popup.setAttribute('aria-modal', 'false');
      this.popup.setAttribute('aria-labelledby', this._titleId);
      this.popup.hidden = true;

      // Header: prev arrow, title, next arrow
      const header = document.createElement('div');
      header.className = 'hn-date__header';

      this.prevBtn = document.createElement('button');
      this.prevBtn.type = 'button';
      this.prevBtn.className = 'hn-date__nav hn-date__nav--prev';
      this.prevBtn.setAttribute('aria-label', 'Previous month');
      this.prevBtn.innerHTML = CHEVRON_LEFT_SVG;

      this.titleEl = document.createElement('div');
      this.titleEl.className = 'hn-date__title';
      this.titleEl.id = this._titleId;
      this.titleEl.setAttribute('aria-live', 'polite');

      this.nextBtn = document.createElement('button');
      this.nextBtn.type = 'button';
      this.nextBtn.className = 'hn-date__nav hn-date__nav--next';
      this.nextBtn.setAttribute('aria-label', 'Next month');
      this.nextBtn.innerHTML = CHEVRON_RIGHT_SVG;

      header.appendChild(this.prevBtn);
      header.appendChild(this.titleEl);
      header.appendChild(this.nextBtn);

      // Day-of-week row
      const dowRow = document.createElement('div');
      dowRow.className = 'hn-date__dow-row';
      dowRow.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < 7; i++) {
        const dow = document.createElement('span');
        dow.className = 'hn-date__dow';
        dow.textContent = DAYS_SHORT[i];
        dowRow.appendChild(dow);
      }

      // Grid container
      this.grid = document.createElement('div');
      this.grid.className = 'hn-date__grid';
      this.grid.id = this._gridId;
      this.grid.setAttribute('role', 'grid');
      this.grid.setAttribute('aria-labelledby', this._titleId);

      // Footer: Today button
      const footer = document.createElement('div');
      footer.className = 'hn-date__footer';
      this.todayBtn = document.createElement('button');
      this.todayBtn.type = 'button';
      this.todayBtn.className = 'hn-date__today-btn';
      this.todayBtn.textContent = 'Today';
      footer.appendChild(this.todayBtn);

      this.popup.appendChild(header);
      this.popup.appendChild(dowRow);
      this.popup.appendChild(this.grid);
      this.popup.appendChild(footer);

      this.appendChild(this.trigger);
      this.appendChild(this.popup);
    }

    _resolveLabelledBy() {
      if (this.native.getAttribute('aria-labelledby')) {
        return this.native.getAttribute('aria-labelledby');
      }
      if (this.native.id) {
        const lbl = document.querySelector('label[for="' + this.native.id + '"]');
        if (lbl) {
          if (!lbl.id) lbl.id = this.native.id + '-label';
          return lbl.id;
        }
      }
      return null;
    }

    _bindEvents() {
      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });

      this.trigger.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          if (!this.isOpen) {
            e.preventDefault();
            this.open();
          }
        }
      });

      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this._stepMonth(-1);
      });

      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this._stepMonth(1);
      });

      this.todayBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const today = new Date();
        if (this._isDisabled(today)) {
          // Today is outside min/max - jump the view but don't select.
          this.viewYear = today.getFullYear();
          this.viewMonth = today.getMonth();
          this.focusedDate = today;
          this._renderCalendar();
          this._focusGridCell();
        } else {
          this._selectDate(today);
        }
      });

      this.grid.addEventListener('click', (e) => {
        const cell = e.target.closest('.hn-date__day');
        if (!cell || cell.disabled) return;
        const iso = cell.dataset.date;
        const d = fromISO(iso);
        if (d) this._selectDate(d);
      });

      // Sync trigger if value mutates externally (e.g. form reset
      // or programmatic assignment from another script).
      this.native.addEventListener('change', () => this.syncFromNative());
      this.native.addEventListener('input', () => this.syncFromNative());

      // If something programmatically focuses the hidden native
      // (e.g. an error-link anchor href="#native-id"), bounce focus
      // to the visible trigger so the user can see their focus.
      this.native.addEventListener('focus', () => {
        if (this._redirectingFocus) return;
        this._redirectingFocus = true;
        // Defer so the focus event finishes before we move focus,
        // avoiding any browser-internal re-entrancy.
        setTimeout(() => {
          this._redirectingFocus = false;
          if (this.trigger && typeof this.trigger.focus === 'function') {
            this.trigger.focus();
          }
        }, 0);
      });
    }

    _stepMonth(delta) {
      let m = this.viewMonth + delta;
      let y = this.viewYear;
      while (m < 0) { m += 12; y--; }
      while (m > 11) { m -= 12; y++; }
      this.viewYear = y;
      this.viewMonth = m;
      this._renderCalendar();
    }

    _selectDate(d) {
      this.native.value = toISO(d);
      this.native.dispatchEvent(new Event('input', { bubbles: true }));
      this.native.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncFromNative();
      this.close();
      this.trigger.focus();
    }

    _getMin() {
      const m = this.native.getAttribute('min');
      return m ? fromISO(m) : null;
    }

    _getMax() {
      const m = this.native.getAttribute('max');
      return m ? fromISO(m) : null;
    }

    _isDisabled(d) {
      const min = this._getMin();
      const max = this._getMax();
      if (min && isBefore(d, min)) return true;
      if (max && isAfter(d, max)) return true;
      return false;
    }

    _isToday(d) {
      const today = new Date();
      return isSameDay(d, today);
    }

    _isSelected(d) {
      const v = this.native.value;
      if (!v) return false;
      const sel = fromISO(v);
      return sel ? isSameDay(d, sel) : false;
    }

    _renderCalendar() {
      this.titleEl.textContent = MONTHS_LONG[this.viewMonth] + ' ' + this.viewYear;

      // Disable nav buttons that would step past min/max.
      const min = this._getMin();
      const max = this._getMax();
      this.prevBtn.disabled = min
        ? (this.viewYear < min.getFullYear() ||
           (this.viewYear === min.getFullYear() && this.viewMonth <= min.getMonth()))
        : false;
      this.nextBtn.disabled = max
        ? (this.viewYear > max.getFullYear() ||
           (this.viewYear === max.getFullYear() && this.viewMonth >= max.getMonth()))
        : false;

      this.grid.textContent = '';
      this.dayCells = [];

      const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
      const firstDow = firstOfMonth.getDay();
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
      const daysInPrevMonth = new Date(this.viewYear, this.viewMonth, 0).getDate();

      // 6 weeks * 7 days = 42 cells. Always render 6 rows so layout
      // height doesn't jump between months.
      for (let i = 0; i < 42; i++) {
        let cellDate;
        let outside = false;

        if (i < firstDow) {
          // Previous month overflow
          const day = daysInPrevMonth - firstDow + 1 + i;
          let y = this.viewYear;
          let m = this.viewMonth - 1;
          if (m < 0) { m = 11; y--; }
          cellDate = new Date(y, m, day);
          outside = true;
        } else if (i < firstDow + daysInMonth) {
          // Current month
          const day = i - firstDow + 1;
          cellDate = new Date(this.viewYear, this.viewMonth, day);
        } else {
          // Next month overflow
          const day = i - firstDow - daysInMonth + 1;
          let y = this.viewYear;
          let m = this.viewMonth + 1;
          if (m > 11) { m = 0; y++; }
          cellDate = new Date(y, m, day);
          outside = true;
        }

        const cell = document.createElement('button');
        cell.type = 'button';
        cell.className = 'hn-date__day';
        cell.setAttribute('role', 'gridcell');
        cell.setAttribute('tabindex', '-1');
        cell.dataset.date = toISO(cellDate);
        cell.textContent = String(cellDate.getDate());

        if (outside) cell.classList.add('hn-date__day--outside');
        if (this._isToday(cellDate)) cell.classList.add('hn-date__day--today');
        if (this._isSelected(cellDate)) {
          cell.classList.add('hn-date__day--selected');
          cell.setAttribute('aria-selected', 'true');
        }
        if (this._isDisabled(cellDate)) {
          cell.disabled = true;
          cell.setAttribute('aria-disabled', 'true');
        }

        const dowLong = DAYS_LONG[cellDate.getDay()];
        cell.setAttribute(
          'aria-label',
          dowLong + ', ' + MONTHS_LONG[cellDate.getMonth()] + ' ' +
          cellDate.getDate() + ', ' + cellDate.getFullYear(),
        );

        this.grid.appendChild(cell);
        this.dayCells.push(cell);
      }

      // Mark the focused cell as tabbable so keyboard nav has a single
      // tab stop inside the grid (roving tabindex pattern).
      this._updateGridTabindex();
    }

    _updateGridTabindex() {
      if (!this.dayCells || !this.dayCells.length) return;
      const targetISO = this.focusedDate ? toISO(this.focusedDate) : null;
      let matchIdx = -1;
      for (let i = 0; i < this.dayCells.length; i++) {
        const cell = this.dayCells[i];
        if (cell.disabled) continue;
        if (targetISO && cell.dataset.date === targetISO) {
          matchIdx = i;
          break;
        }
      }
      // Fall back: first non-disabled cell in the current view.
      if (matchIdx < 0) {
        for (let i = 0; i < this.dayCells.length; i++) {
          if (!this.dayCells[i].disabled &&
              !this.dayCells[i].classList.contains('hn-date__day--outside')) {
            matchIdx = i;
            break;
          }
        }
      }
      for (let i = 0; i < this.dayCells.length; i++) {
        this.dayCells[i].setAttribute('tabindex', i === matchIdx ? '0' : '-1');
      }
      this._focusableIdx = matchIdx;
    }

    _focusGridCell() {
      if (this._focusableIdx >= 0 && this.dayCells[this._focusableIdx]) {
        this.dayCells[this._focusableIdx].focus();
      }
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');

      // Portal the popup to <body> so it escapes any ancestor
      // stacking context (e.g. a parent with transform applied,
      // which traps fixed-positioned descendants) and any
      // overflow:hidden ancestor that would clip it. The trigger
      // stays put; we re-link them with absolute fixed-position
      // coordinates calculated from getBoundingClientRect.
      if (this.popup.parentNode !== document.body) {
        document.body.appendChild(this.popup);
      }
      this.popup.hidden = false;

      // Seed the view from the current value, or today.
      const seed = this.native.value ? fromISO(this.native.value) : new Date();
      this.viewYear = seed.getFullYear();
      this.viewMonth = seed.getMonth();
      this.focusedDate = seed;

      this._renderCalendar();
      // Position must happen AFTER the popup is unhidden + content
      // rendered so offsetHeight / offsetWidth are real.
      this._positionPopup();
      // Defer the focus + listener wiring until after the current
      // event loop tick so the opening click doesn't immediately
      // bubble into our outside-click handler and close us again.
      setTimeout(() => {
        if (!this.isOpen) return;
        this._focusGridCell();
        document.addEventListener('pointerdown', this._onDocPointerDown, true);
        document.addEventListener('keydown', this._onDocKeydown, true);
        window.addEventListener('resize', this._onWinChange);
        // Capture-phase scroll so we follow inner scrolling
        // ancestors too (Shopify themes nest scrollable wrappers).
        window.addEventListener('scroll', this._onWinChange, true);
      }, 0);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.popup.hidden = true;
      document.removeEventListener('pointerdown', this._onDocPointerDown, true);
      document.removeEventListener('keydown', this._onDocKeydown, true);
      window.removeEventListener('resize', this._onWinChange);
      window.removeEventListener('scroll', this._onWinChange, true);
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    _onDocPointerDown(e) {
      // Trigger lives inside `this`; popup is portaled to <body>,
      // so it's no longer a descendant of `this`. Check both.
      if (this.contains(e.target)) return;
      if (this.popup && this.popup.contains(e.target)) return;
      this.close();
    }

    _onDocKeydown(e) {
      if (!this.isOpen) return;
      const key = e.key;

      if (key === 'Escape') {
        e.preventDefault();
        this.close();
        this.trigger.focus();
        return;
      }
      if (key === 'Tab') {
        // Let the browser handle Tab natively but close the popup
        // so focus doesn't get trapped inside it.
        this.close();
        return;
      }

      // Only handle navigation when focus is inside the grid.
      if (!this.grid.contains(e.target)) return;

      let handled = true;
      const f = this.focusedDate || new Date();
      let next = null;

      if (key === 'ArrowLeft') next = this._addDays(f, -1);
      else if (key === 'ArrowRight') next = this._addDays(f, 1);
      else if (key === 'ArrowUp') next = this._addDays(f, -7);
      else if (key === 'ArrowDown') next = this._addDays(f, 7);
      else if (key === 'Home') next = this._addDays(f, -f.getDay());
      else if (key === 'End') next = this._addDays(f, 6 - f.getDay());
      else if (key === 'PageUp') next = this._addMonths(f, e.shiftKey ? -12 : -1);
      else if (key === 'PageDown') next = this._addMonths(f, e.shiftKey ? 12 : 1);
      else if (key === 'Enter' || key === ' ') {
        e.preventDefault();
        if (!this._isDisabled(f)) this._selectDate(f);
        return;
      } else {
        handled = false;
      }

      if (handled && next) {
        e.preventDefault();
        // Clamp to min/max
        const min = this._getMin();
        const max = this._getMax();
        if (min && isBefore(next, min)) next = min;
        if (max && isAfter(next, max)) next = max;

        const movedToOtherMonth =
          next.getFullYear() !== this.viewYear ||
          next.getMonth() !== this.viewMonth;
        this.focusedDate = next;
        if (movedToOtherMonth) {
          this.viewYear = next.getFullYear();
          this.viewMonth = next.getMonth();
          this._renderCalendar();
        } else {
          this._updateGridTabindex();
        }
        this._focusGridCell();
      }
    }

    _addDays(d, delta) {
      const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      r.setDate(r.getDate() + delta);
      return r;
    }

    _addMonths(d, delta) {
      const r = new Date(d.getFullYear(), d.getMonth(), 1);
      r.setMonth(r.getMonth() + delta);
      // Clamp day to last valid day of the target month.
      const lastDay = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
      r.setDate(Math.min(d.getDate(), lastDay));
      return r;
    }

    syncFromNative() {
      const value = this.native.value;
      if (value) {
        this.valueEl.textContent = formatISODate(value);
        this.valueEl.classList.remove('hn-date__value--placeholder');
      } else {
        this.valueEl.textContent = this.native.getAttribute('data-placeholder') || '';
        this.valueEl.classList.add('hn-date__value--placeholder');
      }
    }
  }

  customElements.define('hn-date', HnDate);
})();
